import dayjs from 'dayjs'
import { ResolvableEvent, nextBoundaryAfter, resolveAt } from 'common/scheduleResolver'

/**
 * What a screen should be showing and how long it may sleep before asking again. Kept apart
 * from the component so the decision can be checked without a browser — it is the part that
 * decides what a customer sees.
 */

// Re-check at least this often even when the next boundary is far away, so a device that
// slept, drifted or crossed a DST change corrects itself instead of playing the wrong thing
// until the following event.
export const MAX_SLEEP_MS = 60 * 60 * 1000

// Never sleep for nothing: a boundary computed as already past would spin the timer.
const MIN_SLEEP_MS = 1000

export interface PlannedSchedule<Target> {
  events: Array<ResolvableEvent & { target: Target | null }>
  fallback: Target | null
}

export interface SchedulePlan<Target> {
  target: Target | null
  sleepMs: number
}

export function planSchedule<Target>(
  schedule: PlannedSchedule<Target>,
  now: number,
  timezone: string
): SchedulePlan<Target> {
  const instant = dayjs(now)
  const winner = resolveAt(schedule.events, instant, timezone)
  const boundary = nextBoundaryAfter(schedule.events, instant, timezone)
  const untilBoundary = boundary ? Math.max(boundary.valueOf() - now, MIN_SLEEP_MS) : MAX_SLEEP_MS

  return {
    target: winner?.event.target ?? schedule.fallback,
    sleepMs: Math.min(untilBoundary, MAX_SLEEP_MS),
  }
}
